import { supabase } from "./supabase.js";
import { getTemperatureRangeBounds, mergeTemperatureHistory } from "./temperature-history.js";

function assertSupabase() {
  if (!supabase) throw new Error("Supabase is not configured.");
  return supabase;
}

export async function loadTalosDashboard(user) {
  const client = assertSupabase();
  const { data: memberships, error: membershipError } = await client
    .from("organization_members")
    .select("organization_id, role, organizations(name)")
    .eq("user_id", user.id)
    .eq("status", "active");

  if (membershipError) throw membershipError;
  const membership = memberships?.find((entry) => entry.role === "super_admin") ?? memberships?.[0];
  if (!membership) throw new Error("This account does not have an active Talos organisation.");

  const [{ data: profile, error: profileError }, { data: devices, error: deviceError }, { data: alerts, error: alertError }, { data: locations, error: locationError }] = await Promise.all([
    client.from("profiles").select("display_name, email, phone_e164").eq("id", user.id).single(),
    client
      .from("devices")
      .select("id, organization_id, location_id, device_code, name, equipment_type, connectivity_type, current_temperature_c, maximum_temperature_c, minimum_temperature_c, alert_delay_minutes, measurement_interval_seconds, logging_interval_seconds, heartbeat_interval_seconds, provisioning_status, status, connection_path, last_seen_at, pending_records, wifi_rssi, wifi_ssid, wifi_channel, station_mac, cellular_operator, cellular_signal_quality, firmware_version, hardware_name, firmware_target_version, firmware_update_status, firmware_update_requested_at, firmware_update_started_at, firmware_update_completed_at, firmware_update_error, sensor_status, relay_enabled, relay_peer_count, settings_revision, settings_applied_revision, settings_applied_at, read_request_id, read_requested_at, read_completed_at, locations(name)")
      .eq("organization_id", membership.organization_id)
      .order("name"),
    client
      .from("alerts")
      .select("id, device_id, severity, kind, title, detail, status, opened_at, acknowledged_at, device:devices!alerts_device_id_fkey(name)")
      .eq("organization_id", membership.organization_id)
      .order("opened_at", { ascending: false })
      .limit(50),
    client.from("locations").select("id, organization_id, name, address").eq("organization_id", membership.organization_id).order("name"),
  ]);

  if (profileError) throw profileError;
  if (deviceError) throw deviceError;
  if (alertError) throw alertError;
  if (locationError) throw locationError;

  const deviceRows = await Promise.all((devices ?? []).map(async (device) => {
    const { data: readings, error } = await client
      .from("temperature_readings")
      .select("temperature_c, recorded_at")
      .eq("device_id", device.id)
      .order("recorded_at", { ascending: false })
      .limit(24);
    if (error) throw error;
    const orderedReadings = [...(readings ?? [])].reverse();
    return {
      ...device,
      location_name: device.locations?.name ?? "Unassigned",
      readings: orderedReadings.map((reading) => Number(reading.temperature_c)),
      recent_readings: orderedReadings.map((reading) => ({
        temperature_c: Number(reading.temperature_c),
        recorded_at: reading.recorded_at,
      })),
    };
  }));

  return {
    profile: {
      display_name: profile.display_name,
      email: profile.email,
      phone_e164: profile.phone_e164,
      role: membership.role,
      organization_name: membership.organizations?.name ?? "Organisation",
      location_name: deviceRows[0]?.location_name ?? "Location",
    },
    organizationId: membership.organization_id,
    devices: deviceRows,
    locations: locations ?? [],
    alerts: (alerts ?? []).map((alert) => ({ ...alert, device_name: alert.device?.name ?? "Device" })),
  };
}

export async function loadDeviceTemperatureHistory({ deviceId, range }) {
  const client = assertSupabase();
  const { start, end } = getTemperatureRangeBounds(range);
  const pageSize = 1000;
  async function loadPages(table, temperatureColumn, timestampColumn = "recorded_at") {
    const rows = [];
    for (let from = 0; ; from += pageSize) {
      let query = client
        .from(table)
        .select(`${temperatureColumn}, ${timestampColumn}`)
        .eq("device_id", deviceId)
        .gte(timestampColumn, start.toISOString())
        .lt(timestampColumn, end.toISOString())
        .order(timestampColumn, { ascending: true })
        .range(from, from + pageSize - 1);
      if (table === "heartbeats") query = query.not(temperatureColumn, "is", null);
      const { data, error } = await query;
      if (error) throw error;
      rows.push(...(data ?? []).map((reading) => ({
        temperature_c: Number(reading[temperatureColumn]),
        recorded_at: reading[timestampColumn],
      })));
      if (!data || data.length < pageSize) break;
    }
    return rows;
  }
  const [permanentReadings, heartbeatReadings] = await Promise.all([
    loadPages("temperature_readings", "temperature_c"),
    loadPages("heartbeats", "current_temperature_c", "received_at"),
  ]);
  return mergeTemperatureHistory(permanentReadings, heartbeatReadings);
}

export async function acknowledgeAlert(alertId, userId) {
  const client = assertSupabase();
  const { error } = await client
    .from("alerts")
    .update({ status: "acknowledged", acknowledged_at: new Date().toISOString(), acknowledged_by: userId })
    .eq("id", alertId)
    .eq("status", "open");
  if (error) throw error;
}

export async function loadTemperatureReport({ deviceIds, days }) {
  const client = assertSupabase();
  const cutoff = new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000).toISOString();
  const pageSize = 1000;
  const rows = [];

  for (let from = 0; ; from += pageSize) {
    const { data, error } = await client
      .from("temperature_readings")
      .select("device_id, temperature_c, recorded_at, received_at, quality, device:devices!temperature_readings_device_id_fkey(device_code,name,locations(name))")
      .in("device_id", deviceIds)
      .gte("recorded_at", cutoff)
      .order("recorded_at", { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    rows.push(...(data ?? []));
    if (!data || data.length < pageSize) break;
  }

  return rows.map((reading) => ({
    device_id: reading.device?.device_code ?? reading.device_id,
    device_name: reading.device?.name ?? "Device",
    location: reading.device?.locations?.name ?? "Location",
    temperature_c: reading.temperature_c,
    recorded_at: reading.recorded_at,
    received_at: reading.received_at,
    quality: reading.quality,
  }));
}

export async function inviteUser({ session, email, displayName, role, organizationId }) {
  const apiUrl = import.meta.env.VITE_TALOS_API_URL?.replace(/\/$/, "");
  if (!apiUrl) throw new Error("Talos API URL is not configured.");
  const response = await fetch(`${apiUrl}/v1/admin/invitations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ email, display_name: displayName, role, organization_id: organizationId }),
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error ?? "Could not send invitation.");
  return payload;
}

function talosApiUrl() {
  const apiUrl = import.meta.env.VITE_TALOS_API_URL?.replace(/\/$/, "");
  if (!apiUrl) throw new Error("Talos API URL is not configured.");
  return apiUrl;
}

async function adminApiRequest(session, path, options = {}) {
  const response = await fetch(`${talosApiUrl()}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
      ...options.headers,
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error ?? "Talos could not complete the admin request.");
  return payload;
}

export function loadProvisioningOptions(session) {
  return adminApiRequest(session, "/v1/admin/provisioning/options");
}

export function createDeviceProvisioning(session, input) {
  return adminApiRequest(session, "/v1/admin/device-provisioning", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function createExistingDeviceProvisioning(session, deviceId, connectivityType) {
  return adminApiRequest(session, `/v1/admin/devices/${encodeURIComponent(deviceId)}/credential`, {
    method: "POST",
    body: JSON.stringify({ connectivity_type: connectivityType }),
  });
}

export function revokeDeviceApiAccess(session, deviceId) {
  return adminApiRequest(session, `/v1/admin/devices/${encodeURIComponent(deviceId)}/credential/revoke`, { method: "POST" });
}

export function deleteUncommissionedDevice(session, deviceId) {
  return adminApiRequest(session, `/v1/admin/devices/${encodeURIComponent(deviceId)}`, { method: "DELETE" });
}

export function loadFleetInventory(session) {
  return adminApiRequest(session, "/v1/admin/fleet");
}

export function createLocation(session, input) {
  return adminApiRequest(session, "/v1/admin/locations", { method: "POST", body: JSON.stringify(input) });
}

export function assignDeviceLocation(session, deviceId, locationId) {
  return adminApiRequest(session, `/v1/admin/devices/${encodeURIComponent(deviceId)}/assignment`, { method: "PATCH", body: JSON.stringify({ location_id: locationId }) });
}

export function transferDeviceOrganization(session, deviceId, organizationId, locationId = null) {
  return adminApiRequest(session, `/v1/admin/devices/${encodeURIComponent(deviceId)}/transfer`, { method: "POST", body: JSON.stringify({ organization_id: organizationId, location_id: locationId }) });
}

export function requestFirmwareUpdate(session, deviceId) {
  return adminApiRequest(session, `/v1/admin/devices/${encodeURIComponent(deviceId)}/firmware-update`, {
    method: "POST",
  });
}

export function uploadDeviceRecoveryReadings(session, deviceId, { readings, source, fileName }) {
  return adminApiRequest(session, `/v1/admin/devices/${encodeURIComponent(deviceId)}/recovery-import`, {
    method: "POST",
    body: JSON.stringify({ readings, source, file_name: fileName || null }),
  });
}

export async function renameDevice(session, deviceId, name) {
  const normalized = name.trim();
  if (normalized.length < 2 || normalized.length > 120) throw new Error("Use a device name between 2 and 120 characters.");
  return adminApiRequest(session, `/v1/admin/devices/${encodeURIComponent(deviceId)}/name`, { method: "PATCH", body: JSON.stringify({ name: normalized }) });
}

export async function updateDeviceSettings(deviceId, settings) {
  const client = assertSupabase();
  const { data, error } = await client.from("devices").update(settings).eq("id", deviceId).select("id, maximum_temperature_c, minimum_temperature_c, alert_delay_minutes, measurement_interval_seconds, logging_interval_seconds, heartbeat_interval_seconds, relay_enabled, settings_revision, settings_applied_revision").single();
  if (error) throw error;
  return data;
}

export async function requestDeviceReading(deviceId) {
  const client = assertSupabase();
  const requestedAt = new Date().toISOString();
  const { data, error } = await client.from("devices").update({ read_request_id: crypto.randomUUID(), read_requested_at: requestedAt }).eq("id", deviceId).select("id, read_request_id, read_requested_at, read_completed_at").single();
  if (error) throw error;
  return data;
}

export async function loadPrivacyCentre({ userId, organizationId }) {
  const client = assertSupabase();
  const [noticeResult, acknowledgementResult, requestsResult, policiesResult, deletionJobsResult] = await Promise.all([
    client.from("privacy_notice_versions").select("id, version, title, notice_url, effective_at").is("retired_at", null).order("effective_at", { ascending: false }).limit(1).maybeSingle(),
    client.from("privacy_notice_acknowledgements").select("notice_version_id, acknowledged_at, method").eq("user_id", userId).eq("organization_id", organizationId),
    client.from("privacy_requests").select("id, reference, request_type, jurisdiction, details, status, requested_at, acknowledged_at, due_at, completed_at, decision_summary").eq("requester_user_id", userId).order("requested_at", { ascending: false }),
    client.from("data_retention_policies").select("category, retention_days, disposition, enforcement_enabled, purpose").eq("organization_id", organizationId).order("category"),
    client.from("data_deletion_jobs").select("privacy_request_id, status, scheduled_for, executed_at, last_error").eq("subject_user_id", userId),
  ]);
  for (const result of [noticeResult, acknowledgementResult, requestsResult, policiesResult, deletionJobsResult]) if (result.error) throw result.error;
  return {
    notice: noticeResult.data ?? null,
    acknowledgements: acknowledgementResult.data ?? [],
    requests: requestsResult.data ?? [],
    policies: policiesResult.data ?? [],
    deletionJobs: deletionJobsResult.data ?? [],
  };
}

export async function acknowledgePrivacyNotice({ userId, organizationId, noticeVersionId }) {
  const client = assertSupabase();
  const { data, error } = await client.from("privacy_notice_acknowledgements").upsert({
    user_id: userId,
    organization_id: organizationId,
    notice_version_id: noticeVersionId,
    acknowledged_at: new Date().toISOString(),
    method: "authenticated_privacy_centre",
  }, { onConflict: "notice_version_id,user_id,organization_id" }).select("notice_version_id, acknowledged_at, method").single();
  if (error) throw error;
  return data;
}

export async function submitPrivacyRequest({ userId, organizationId, requestType, jurisdiction, details }) {
  const client = assertSupabase();
  const { data, error } = await client.from("privacy_requests").insert({
    requester_user_id: userId,
    organization_id: organizationId,
    request_type: requestType,
    jurisdiction,
    details: details.trim(),
  }).select("id, reference, request_type, jurisdiction, details, status, requested_at, acknowledged_at, due_at, completed_at, decision_summary").single();
  if (error) throw error;
  return data;
}

export async function updateOwnProfile(userId, input) {
  const client = assertSupabase();
  const displayName = input.display_name.trim();
  const phone = input.phone_e164.trim();
  if (displayName.length < 2 || displayName.length > 100) throw new Error("Use a name between 2 and 100 characters.");
  if (phone && !/^\+[1-9][0-9]{7,14}$/.test(phone)) throw new Error("Use an international phone number such as +919876543210, or leave it blank.");
  const { data, error } = await client.from("profiles").update({ display_name: displayName, phone_e164: phone || null }).eq("id", userId).select("display_name, email, phone_e164").single();
  if (error) throw error;
  return data;
}

export async function downloadPersonalData(session) {
  const response = await fetch(`${talosApiUrl()}/v1/privacy/export`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error ?? "Talos could not prepare the export.");
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = disposition.match(/filename="([^"]+)"/)?.[1] ?? `talos-personal-data-${new Date().toISOString().slice(0, 10)}.json`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function loadPrivacyAdminRequests(session) {
  return adminApiRequest(session, "/v1/admin/privacy-requests");
}

export function updatePrivacyAdminRequest(session, requestId, input) {
  return adminApiRequest(session, `/v1/admin/privacy-requests/${encodeURIComponent(requestId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}
